
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Settings2 } from 'lucide-react';
import { useGetProcurementById } from '@/hooks/use-procurement';
import { useGetSupplierById } from '@/hooks/use-supplier';
import { useGetAgentById } from '@/hooks/use-agent';
import { useAuth } from '@/contexts/AuthContext';
import { RequestorInfoSection } from '@/components/procurement-approval/RequestorInfoSection';
import { ProductInfoSection } from '@/components/procurement-approval/ProductInfoSection';
import { SourceInfoSection } from '@/components/procurement-approval/SourceInfoSection';
import { CostBreakdownSection } from '@/components/procurement-approval/CostBreakdownSection';
import { PaymentTermsSection } from '@/components/procurement-approval/PaymentTermsSection';
import { NotesSection } from '@/components/procurement-approval/NotesSection';
import { ApprovalActions } from '@/components/procurement-approval/ApprovalActions';
import { WarehouseInspectionSection } from '@/components/procurement-approval/WarehouseInspectionSection';
import { QualityInspectionSection } from '@/components/procurement-approval/QualityInspectionSection';
import { ProcurementItemsList } from '@/components/procurements/ProcurementItemsList';
import { ProcurementApprovalHeader } from '@/components/procurement-approval/ProcurementApprovalHeader';
import { AdminAdjustmentDialog } from '@/components/procurements/admin/AdminAdjustmentDialog';
import ApproveConfirmationDialog from '@/components/finance/ApproveConfirmationDialog';
import RejectConfirmationDialog from '@/components/finance/RejectConfirmationDialog';

const ProcurementApprovalPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isApproveDialogOpen, setIsApproveDialogOpen] = useState(false);
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);
  const [showAdjustmentDialog, setShowAdjustmentDialog] = useState(false);
  
  const { data: procurement, isLoading } = useGetProcurementById(id || '');
  
  const canAdminAdjust = user?.role === 'admin' && procurement?.status === 'pending_finance';
  
  // Fetch source details based on source type
  const { data: supplierData } = useGetSupplierById(
    procurement?.source_type === 'supplier' ? procurement.source_id || '' : ''
  );
  
  const { data: agentData } = useGetAgentById(
    procurement?.source_type === 'agent' ? procurement.source_id || '' : ''
  );

  const handleBack = () => {
    navigate('/finance');
  };

  const handleUpdateCosts = async (costs: any) => {
    // This is a read-only page for approval, so we don't allow cost updates
    console.log('Cost updates not allowed on approval page');
  };

  if (isLoading) {
    return (
      <Layout fullWidth hideHeader>
        <div className="min-h-screen p-4">
          <div className="space-y-8">
            <Skeleton className="h-12 w-3/4" />
            <Skeleton className="h-96 w-full" />
          </div>
        </div>
      </Layout>
    );
  }

  if (!procurement) {
    return (
      <Layout fullWidth hideHeader>
        <div className="min-h-screen p-4">
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <p className="text-xl font-medium">Procurement not found</p>
              <p className="text-muted-foreground mt-2">The procurement you're looking for doesn't exist or you don't have access to it.</p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  // Get source details based on source type
  const getSourceDetails = () => {
    if (procurement.source_type === 'supplier' && supplierData) {
      return {
        name: supplierData.name,
        contact: supplierData.phone_number,
        address: supplierData.address || 'N/A',
        bankName: supplierData.bank_name || 'N/A',
        bankAccount: supplierData.bank_account || 'N/A',
        bankBranch: supplierData.bank_branch || 'N/A',
      };
    } else if (procurement.source_type === 'agent' && agentData) {
      return {
        name: agentData.name,
        contact: agentData.phone_number,
        address: agentData.address || 'N/A',
        bankName: agentData.bank_name || 'N/A',
        bankAccount: agentData.bank_account || 'N/A',
        bankBranch: agentData.bank_branch || 'N/A',
      };
    } else {
      return {
        name: 'N/A',
        contact: 'N/A',
        address: 'N/A',
        bankName: 'N/A',
        bankAccount: 'N/A',
        bankBranch: 'N/A',
      };
    }
  };
  
  const sourceDetails = getSourceDetails();

  return (
    <Layout fullWidth hideHeader>
      <div className="min-h-screen w-full p-4">
        <div className="w-full space-y-6">
          <Card className="w-full shadow-lg">
            <ProcurementApprovalHeader 
              onBack={handleBack}
              status={procurement.status}
              actions={canAdminAdjust && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAdjustmentDialog(true)}
                >
                  <Settings2 className="mr-2 h-4 w-4" />
                  Adjust Data
                </Button>
              )}
            />
            
            <CardContent className="p-6 space-y-6">
              <RequestorInfoSection procurement={procurement} />
              
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <div className="space-y-6">
                  <ProductInfoSection procurement={procurement} />
                  
                  {/* Product Breakdown Section */}
                  {procurement.items && procurement.items.length > 0 && (
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold">Product Breakdown</h3>
                      <ProcurementItemsList 
                        items={procurement.items} 
                        showTitle={false}
                      />
                    </div>
                  )}
                </div>
                
                <div className="space-y-6">
                  <SourceInfoSection 
                    procurement={procurement} 
                    sourceDetails={sourceDetails} 
                  />
                  
                  <PaymentTermsSection procurement={procurement} />
                </div>
              </div>
              
              <div className="border-t pt-6">
                <CostBreakdownSection 
                  procurement={procurement} 
                  onUpdateCosts={handleUpdateCosts}
                  isLoading={false}
                />
              </div>
              
              {/* Quality Inspection Section - Always show for all procurements */}
              <div className="border-t pt-6">
                <QualityInspectionSection procurement={procurement} />
              </div>
              
              {/* Warehouse Inspection Section - Only show for payment terms (not upfront) */}
              {procurement.finance?.payment_term_type === 'term' && (
                <div className="border-t pt-6">
                  <WarehouseInspectionSection procurement={procurement} />
                </div>
              )}
              
              {procurement.notes && (
                <div className="border-t pt-6">
                  <NotesSection notes={procurement.notes} />
                </div>
              )}
            </CardContent>
            
            <div className="border-t bg-gray-50">
              <ApprovalActions
                onCancel={handleBack}
                onReject={() => setIsRejectDialogOpen(true)}
                onApprove={() => setIsApproveDialogOpen(true)}
                status={procurement.status}
              />
            </div>
          </Card>
        </div>
      </div>
      
      <ApproveConfirmationDialog
        isOpen={isApproveDialogOpen}
        onClose={() => setIsApproveDialogOpen(false)}
        procurementId={procurement.id}
        onSuccess={handleBack}
      />
      
      <RejectConfirmationDialog
        isOpen={isRejectDialogOpen}
        onClose={() => setIsRejectDialogOpen(false)}
        procurementId={procurement.id}
        onSuccess={handleBack}
      />
      
      {procurement && (
        <AdminAdjustmentDialog
          open={showAdjustmentDialog}
          onOpenChange={setShowAdjustmentDialog}
          procurement={procurement}
        />
      )}
    </Layout>
  );
};

export default ProcurementApprovalPage;
